import { isCanvasOnPage, removeFromRenderTree } from '../canvas/render-tree';
import { EventEmitter, Injectable, Output } from "@angular/core"
import { AdjacencyTypes, Animator, CanvasComponent, ExteriorNodeLabelModel, GradientStop, GraphComponent, HeatMapRenderer, IEdge, IEnumerable, IEnumerableConvertible, IGraph, ILabelModelParameter, INode, IRenderTreeElement, SvgVisual, TimeSpan } from '@yfiles/yfiles'
import { LABEL_TYPES } from "../model-services/model-item-enums"
import { TemporalLabelStyleDecorator } from "./temporal-label-style-decorator"
import { ACTION_FREQUENCY_TYPE, Frequency, FREQUENCY_TYPE, getDurationHours, getInsights, getInsightsForNode, LinqNode, Linqset, LinqsetNode, NODE_SUPERTYPE, WORK_TYPE } from "../common/linqset-model"
import { LAYOUT_DIRECTION } from "../common/user"
import { processLinqset } from "../common/model-processing"
import { LinqsetRepositoryService } from "../model-services/linqset-repository.service"
import { gradients } from "./heat-gradients"
import { MAX_QUEUE_FOR_COLOR, TemporalNode } from "./temporal-model"
import { createPacketElement, EdgeStyle, PacketAnimation, svgns } from "../canvas/styles/edge-style"
import { SimulationSnapshot } from "./simulation-report"
import { createTemporalNode, SECONDS_PER_YEAR, SimulationRun, stepSettings } from "./simulation-run"

export const MINUTES_PER_HOUR = 60
export const MINUTES_IN_DAY = 360*8
export { SECONDS_PER_YEAR }

export const REAL_SECONDS_PER_TICK = 0.2      // actual wait time between ticks being run
const SECONDS_PER_WORKING_DAY = 8*60*MINUTES_PER_HOUR
const MIN_MILLISECONDS_BETWEEN_DRAWS = 400

// edges drawn by a style without packets of their own (EdgeStyle has them) show items moving along them in this colour
const PACKET_COLOUR = '#0f8b8d'

// is produced at the end of each tick
// defines the number of inputs procssed and then for each output node the number processed by that node and the starting tick for the last item processed
export class TickStatistics {
    constructor(public tick:number, public received:number, public outputs:{_key:string, name:string, processed:number, ticks:number, nodelaySeconds:number}[]) {}
}

// this function provides the heat for a node or edge at a specific time
// it is used to update the heat map or the node decoration
const getHeat = (item: INode | IEdge): number => {
    // we define the heat as the current queue length for the node
    const queue = item instanceof INode ? item.tag?.temporalNode?.queue||0 : 0
    return Math.min(1, queue/MAX_QUEUE_FOR_COLOR)
}

@Injectable()
export class TemporalService {
    graphComponent:GraphComponent
    startingNodes:{node:INode, itemsPerYear:number}[] = []
    lastNodes:INode[] = []

    secondsPerTick:number = 60

    isRunning:boolean = false

    // what's simulated, with the graph's nodes and edges drawing it
    private run?:SimulationRun

    labelStyle = new TemporalLabelStyleDecorator()
    
    animator:Animator

    @Output() statisticsEvent = new EventEmitter<TickStatistics>()

    // a run sets each capture's frequency to the simulated inputs; the model's own values are put back when it is cleared
    private originalFrequencies = new Map<LinqsetNode, Frequency>()
    private simulatedLinqset:Linqset
    private heatMapElement?:IRenderTreeElement
    private packetElement?:IRenderTreeElement
    private packetEdges:IEdge[] = []
    private timer?:number
    private lastDrawn = 0
    private onPath = new Set<INode>()       // the nodes on the way to the one being set up, so loops can be found

    constructor(private repository:LinqsetRepositoryService) {
        this.isRunning = false
    }

    // stops the simulation where it is, leaving its queues showing so it can be continued or cleared
    public stopAnimation() {
        this.isRunning = false
        window.clearTimeout(this.timer)
        this.timer = undefined
        // the last ticks may not have been drawn
        this.graphComponent?.invalidate()
    }

    get tickNumber() {
        return this.run?.tickNumber ?? 1
    }

    get ticksPerYear() {
        return this.run?.ticksPerYear ?? 0
    }

    get totalReceived() {
        return this.run?.totalReceived ?? 0
    }

    // whether a simulation is showing, running or stopped
    get hasSimulation() {
        return !!this.graphComponent && !!this.run
    }

    // whether the simulated year has run its course
    get isFinished() {
        return this.hasSimulation && this.run!.finished
    }

    // carries on a stopped simulation from where it was stopped
    public continueAnimation() {
        if (this.isRunning || !this.hasSimulation || this.isFinished) return
        this.isRunning = true
        this.startSimulator()
    }

    // how far through the year the simulation is, and how many items have gone in, come out and are waiting
    get progress() {
        const seconds = Math.max(0, this.tickNumber-1)*this.secondsPerTick
        const day = Math.floor(seconds/SECONDS_PER_WORKING_DAY)
        const completed = this.lastNodes.reduce((sum, n) => sum + (n.tag?.temporalNode?.finished.length || 0), 0)
        const queued = this.graphComponent ? this.graphComponent.graph.nodes.reduce((sum, n) => sum + (n.tag?.temporalNode?.queue || 0), 0) : 0
        return { week: Math.floor(day/5) + 1, day: day%5 + 1, weeks: Math.round(SECONDS_PER_YEAR/SECONDS_PER_WORKING_DAY/5), received: Math.round(this.totalReceived), completed, queued }
    }

    // the simulation as it stands, for its results
    snapshot():SimulationSnapshot|undefined {
        return this.hasSimulation ? this.run!.snapshot() : undefined
    }

    public clear() {
        this.stopAnimation()
        this.restoreFrequencies()
        this.run = undefined
        if (this.graphComponent) {
            // the endless animation and its heat map would otherwise stay, one more for each run
            this.animator?.stop()
            // a canvas whose page has gone has been cleaned up: its graph can't be tidied, and needn't be
            if (isCanvasOnPage(this.graphComponent)) {
                this.graphComponent.graph.nodes.forEach(node => {
                    const label = node.labels && node.labels.find(l => l.tag && l.tag.type === LABEL_TYPES.QUEUE)
                    if (label) this.graphComponent.graph.remove(label)
                })
                // only the simulation's part of each tag goes: graphs keep other things there, such as how a node is drawn
                this.graphComponent.graph.nodes.filter(n => n.tag?.temporalNode).forEach(n => {
                    const tag = {...n.tag}
                    delete tag.temporalNode
                    n.tag = tag
                })
                removeFromRenderTree(this.graphComponent, this.heatMapElement)
                removeFromRenderTree(this.graphComponent, this.packetElement)
                this.packetEdges.forEach(e => { if (e.tag) delete e.tag.packetAnimator })
                this.graphComponent.invalidate()
            }
            this.statisticsEvent.emit(null)
            this.heatMapElement = undefined
            this.packetElement = undefined
            this.packetEdges = []
        }
    }

    private restoreFrequencies() {
        if (this.originalFrequencies.size === 0) return
        this.originalFrequencies.forEach((frequency, node) => node.properties.frequency = frequency)
        this.originalFrequencies.clear()
        // the insights were worked out from the simulated inputs, so work them out again
        const linqset = this.simulatedLinqset
        this.simulatedLinqset = undefined
        if (linqset) processLinqset(linqset, this.repository.organisationLinqset, true, linqset.organisation)
    }

    public async runAnimation(graphComponent:GraphComponent, minutesPerTickSeconds:number, linqset?:Linqset, nodes?:{node:INode, itemsPerYear:number}[], rolesMap?:any) {
        this.clear()
        
        this.graphComponent = graphComponent
        // initialize the heat map and start the animation
        const getElapsedTime = this.startAnimation(graphComponent)
        const heatProvider = this.createHeatProvider(getElapsedTime)
        const heatMapRenderer = this.initializeHeatMap(heatProvider, gradients[3].value)
        
        this.secondsPerTick = 60*minutesPerTickSeconds*REAL_SECONDS_PER_TICK

        this.startingNodes = []
        this.lastNodes = []
        const layout = this.graphComponent.graph.tag?.orientation === LAYOUT_DIRECTION.LEFT_RIGHT || this.graphComponent.graph.tag?.orientation === LAYOUT_DIRECTION.RIGHT_LEFT ? ExteriorNodeLabelModel.TOP : ExteriorNodeLabelModel.RIGHT
        if (nodes) {
            this.simulatedLinqset = linqset
            nodes.forEach(n => {
                this.startingNodes.push({node:n.node, itemsPerYear:n.itemsPerYear})
                const node:LinqsetNode = n.node.tag.node
                if (!this.originalFrequencies.has(node)) this.originalFrequencies.set(node, node.properties.frequency)
                node.properties.frequency = {type:FREQUENCY_TYPE.YEAR, ocurrences:n.itemsPerYear} as Frequency
            })
        }else {
            // items come in where the graph starts, and at actions that set how often they happen, as the insights do
            this.startingNodes = graphComponent.graph.nodes.filter(n => n.tag.node.isStarting || n.tag.node.setsFrequency).map(n => {return {node:n, itemsPerYear:n.tag.node.items}}).toArray()
        }

        if (linqset) {
            await processLinqset(linqset, this.repository.organisationLinqset, true, linqset.organisation)
        }

        const sources = this.startingNodes.map(n => {
            const tn = this.addTemporalNode(n.node, graphComponent.graph, layout, linqset, rolesMap)
            // an action with its own frequency does that much whatever reaches it, so what reaches it goes no further
            if (!nodes && n.node.tag.node.setsFrequency && !n.node.tag.node.isStarting) tn.setsOwnVolume = true
            return {node:tn, itemsPerYear:n.itemsPerYear, proportionLeft:0}
        })
        this.addPackets(graphComponent)
        const predecessors = new Map<string, string[]>()
        graphComponent.graph.edges.forEach(e => {
            const from = e.sourceNode?.tag?.temporalNode, to = e.targetNode?.tag?.temporalNode
            if (!from || !to) return
            const list = predecessors.get(to._key)
            if (list) list.push(from._key)
            else predecessors.set(to._key, [from._key])
        })
        const simulated = graphComponent.graph.nodes.filter(n => n.tag?.temporalNode).map(n => ({node:n.tag.temporalNode as TemporalNode, info:n.tag.node})).toArray()
        this.run = new SimulationRun(this.secondsPerTick, sources, simulated, this.lastNodes.map(n => ({node:n.tag.temporalNode, info:n.tag.node})), predecessors)

        this.isRunning = true

        this.startSimulator()
    }

    /**
        * Starts an infinite animation and returns a function that returns the elapsed time in milliseconds.
    */
    private startAnimation(canvasComponent: CanvasComponent): () => number {
        const startTimeMilliseconds = Date.now()
        let elapsedMilliseconds = 0
        const updateElapsedMilliseconds = (): void => {
            elapsedMilliseconds = Date.now() - startTimeMilliseconds
        }

        this.animator = new Animator({ canvasComponent, allowUserInteraction: true })
        this.animator.allowUserInteraction = true
        this.animator.autoInvalidation = false

        void this.animator.animate(updateElapsedMilliseconds, TimeSpan.MAX_VALUE)
        return () => elapsedMilliseconds
    }

    /**
        * Creates the heat function that returns the heat value for a given item at a given time.
    */
    private createHeatProvider(getElapsedTime: () => number) {
        return (item: INode | IEdge) => {
            return getHeat(item)
            //const tag = item.tag as ItemTag | null
            //return tag?.getHeat(getElapsedTime()) ?? 0
        }
    }

    /**
        * Initializes the {@link HeatMapRenderer} and adds it to the background group of the render tree.
    */
    private initializeHeatMap(heatProvider: (item: INode | IEdge) => number, gradient: IEnumerable<GradientStop> | IEnumerableConvertible<GradientStop>): HeatMapRenderer {
        const renderTree = this.graphComponent.renderTree
        const heatMapRenderer = new HeatMapRenderer({
            nodeHeatProvider: heatProvider,
            edgeHeatProvider: heatProvider,
            gradient
        })
        this.heatMapElement = renderTree.createElement(renderTree.backgroundGroup, this.graphComponent.graph, heatMapRenderer)
        return heatMapRenderer
    }

    // edges drawn by a style without packets get theirs in a layer above the graph, so items can be seen moving
    // along them whichever style draws the graph
    private addPackets(graphComponent:GraphComponent) {
        const g = window.document.createElementNS(svgns, 'g')
        this.packetEdges = graphComponent.graph.edges.filter(e => e.tag && e.sourceNode?.tag?.temporalNode && !(e.style instanceof EdgeStyle)).toArray()
        this.packetEdges.forEach(e => e.tag.packetAnimator = new PacketAnimation(createPacketElement(g, PACKET_COLOUR), e))
        if (this.packetEdges.length) this.packetElement = graphComponent.renderTree.createElement(graphComponent.renderTree.contentGroup, new SvgVisual(g))
    }

    private addTemporalNode(node:INode, graph:IGraph, layout:ILabelModelParameter, linqset?:Linqset, rolesMap?:any) {
        if (node.tag.temporalNode) return node.tag.temporalNode

        const orientation = graph.tag?.orientation || LAYOUT_DIRECTION.DOWN
        let layoutParameter = orientation === LAYOUT_DIRECTION.LEFT_RIGHT?ExteriorNodeLabelModel.TOP:ExteriorNodeLabelModel.RIGHT

        const ln = node.tag.node
        if (ln.superType === NODE_SUPERTYPE.ACTION) {
            let numPeople = ln.numPeople||0
            let duration = ln.durationSeconds||0
            let capacity = ln.capacity||0
            let hours = ln.hours||0
            const processType = ln.insights?ln.properties?.actionProcessType:ln.processType
            let waits = ln.workKind === WORK_TYPE.WAITING

            if (linqset && ln.insights) {
                duration = getDurationHours(ln.properties.duration)*3600
                const i = getInsightsForNode(ln, ln.modelKey, linqset?.workspaceKey)
                numPeople = i.numPeople
                hours = i.hours
                waits = i.workKind === WORK_TYPE.WAITING
                capacity = (i.roleKey && rolesMap[i.roleKey])?rolesMap[i.roleKey].number*rolesMap[i.roleKey].capacity:i.modelledCapacity
            }

            node.tag.temporalNode = createTemporalNode(ln, stepSettings(this.secondsPerTick, duration, numPeople, hours, capacity, processType, waits))

            // shows how many items wait at the step, when any do
            this.graphComponent.graph.addLabel({owner:node, text:'', layoutParameter, style:this.labelStyle, tag:{
                type: LABEL_TYPES.QUEUE
            }})
        }else {
            node.tag.temporalNode = createTemporalNode(ln)
        }
        node.tag.temporalNode.animator = this.animator
        const edges = graph.edgesAt(node, AdjacencyTypes.OUTGOING)
        if (edges.size === 0) {
            this.lastNodes.push(node)
        }else {
            this.onPath.add(node)
            edges.forEach(e => {
                // a connection back to a step on the way here (rework, say) closes a loop, which the insights don't go
                // round either: items copied around one would multiply without end
                if (e.targetNode.tag?.node && !this.onPath.has(e.targetNode)) {
                    const tn = this.addTemporalNode(e.targetNode, graph, layout, linqset, rolesMap)
                    // model edges give the share of items sent as outputPercentage, analysis edges as percentage
                    node.tag.temporalNode.addNext(tn, e.tag.edge.outputPercentage ?? e.tag.edge.percentage, e)
                }
            })
            this.onPath.delete(node)
        }

        return node.tag.temporalNode
    }
    

    /**
    * Starts the simulator.
    */
    private startSimulator() {
        const t = REAL_SECONDS_PER_TICK*1000
        this.timer = window.setTimeout(() => {
            this.timer = undefined
            if (!this.isRunning || !this.run || this.run.finished) {
                this.isRunning = false
                return
            }else {
                this.tick()
                this.run.tickNumber++
                this.startSimulator()
            }
        }, t)
    }

    /**
     * Performs one step in the simulation.
     */
    private tick() {
        this.run!.tick()

        const visited = new Set<TemporalNode>()
        this.startingNodes.forEach(n => {
            this.animateItemsSent(n.node.tag.temporalNode, visited)
        })
              
        // drawing a large graph takes longer than a tick, so it's redrawn a few times a second rather than every tick
        const now = Date.now()
        if (now - this.lastDrawn >= MIN_MILLISECONDS_BETWEEN_DRAWS) {
            this.lastDrawn = now
            this.graphComponent.canvasContext.canvasComponent.updateVisual()
        }

        const outputs = this.lastNodes.map(n => {
            const ln = n.tag.node
            const length = n.tag.temporalNode.finished.length
            const ticks = length===0?0:n.tag.temporalNode.finished[length-1].endTick-n.tag.temporalNode.finished[length-1].startTick
            const nodelaySeconds = length===0?0:n.tag.temporalNode.finished[length-1].noQueueTime

            return {_key:ln._key, name:ln.name, processed:n.tag.temporalNode.finished.length, ticks, nodelaySeconds}
        })

        this.statisticsEvent.emit(new TickStatistics(this.tickNumber, Math.round(this.totalReceived), outputs))
        
    }    

    // each step once: following every route instead takes exponentially long where routes split and rejoin, and
    // never ends in a loop
    private animateItemsSent(tNode: TemporalNode, visited: Set<TemporalNode>) {
        if (visited.has(tNode)) return
        visited.add(tNode)
        tNode.next.forEach(n => {
            if (n.edge?.tag?.packetAnimator && n.itemsSent > 0) n.edge.tag.packetAnimator.sendPacket(this.animator)
            this.animateItemsSent(n.node, visited)
        })
    }
}